// === EditCMTDialog.jsx (Updated to Highlight Changed Fields) ===

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Chip,
  Avatar,
  useTheme,
  Grid,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  Close,
  Email,
  Phone,
  AccountCircle,
  SmsRounded,
  Check,
  Save,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import axios from "axios";

const statusColors = {
  pending: "warning",
  active: "success",
  inactive: "default",
};

const EditCMTDialog = ({
  open,
  onClose,
  user,
  onChangeStatus,
  onUserUpdate,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
  });
  const [originalData, setOriginalData] = useState({
    username: "",
    email: "",
    phone: "",
  });
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    if (user) {
      const initData = {
        username: user.username || "",
        email: user.email || "",
        phone: user.phone || "",
      };
      setFormData(initData);
      setOriginalData(initData);
    }
  }, [user]);

  const handleStatusChange = (newStatus) => {
    if (newStatus !== user.status) {
      onChangeStatus(newStatus);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isChanged = (key) => formData[key] !== originalData[key];

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/updateCMT/${user.id}`,
        {
          username: formData.username,
          email: formData.email,
          phone: formData.phone,
        },
        { withCredentials: true }
      );

      if (res.status === 200) {
        setSnackbarOpen(true);
        onUserUpdate(); // refresh table
        onClose();
      }
    } catch (error) {
      console.error("Failed to save changes", error);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        // PaperProps={{ sx: { borderRadius: 3, bgcolor: colors.primary[800] } }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.1rem",
            bgcolor: colors.primary[600],
            color: "#fff",
            py: 1.5,
            px: 3,
          }}
        >
          CMT Information
        </DialogTitle>

        <DialogContent>
          <Box
            mt={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Avatar
              sx={{
                width: 70,
                height: 70,
                bgcolor: colors.greenAccent[500],
                fontWeight: "bold",
                fontSize: "1.2rem",
              }}
            >
              <Typography variant="h2">
                {user.fullname.charAt(0).toUpperCase()}
              </Typography>
            </Avatar>
            <Typography variant="h6" gutterBottom>
              {user?.fullname}
            </Typography>
            <Box display="flex" justifyContent="center" gap={2}>
              <Chip
                icon={user?.status === "active" ? <CheckCircle /> : <Cancel />}
                label={user?.status.toUpperCase()}
                color={
                  user?.status === "active"
                    ? "success"
                    : user?.status === "pending"
                    ? "primary"
                    : "error"
                }
                size="small"
              />
              <Chip
                icon={user?.verified ? <Check /> : <Cancel />}
                label={user?.verified ? "Verified" : "Non-Verified"}
                color={user?.verified ? "success" : "default"}
                size="small"
              />
            </Box>
          </Box>

          <Grid container spacing={1} mt={2}>
            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: isChanged("username")
                  ? "rgba(255, 255, 0, 0.08)"
                  : undefined,
              }}
            />
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: isChanged("email")
                  ? "rgba(255, 255, 0, 0.08)"
                  : undefined,
              }}
            />
            <TextField
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone />
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: isChanged("phone")
                  ? "rgba(255, 255, 0, 0.08)"
                  : undefined,
              }}
            />
            <TextField
              label="Communication Method"
              value={
                user.communication_method === "both"
                  ? "WhatsApp, Email"
                  : user.communication_method
              }
              disabled
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SmsRounded />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{ justifyContent: "space-between", px: 3, pb: 2, pt: 1 }}
        >
          <Box display="flex" gap={1}>
            {user.status === "pending" && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => handleStatusChange("active")}
                >
                  Activate
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Cancel />}
                  onClick={() => handleStatusChange("inactive")}
                >
                  Deactivate
                </Button>
              </>
            )}
            {user.status === "active" && (
              <Button
                variant="contained"
                color="error"
                startIcon={<Cancel />}
                onClick={() => handleStatusChange("inactive")}
                sx={{color:"#fff"}}
              >
                Deactivate
              </Button>
            )}
            {user.status === "inactive" && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => handleStatusChange("active")}
                sx={{color:"#fff"}}
              >
                Activate
              </Button>
            )}
          </Box>

          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              color="info"
              startIcon={<Save />}
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? "Saving..." : "Confirm"}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Close />}
              onClick={onClose}
            >
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          CMT profile updated successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditCMTDialog;
